'use client';

import { readMultimodalReferenceTokens } from '@/ai/references/multimodal-reference-placeholder';
import { toast } from '@/lib/ui/toast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WorkspaceReferenceImage } from '../contracts/runtime-state';
import { polishWorkspacePrompt } from './workspace-prompt-polish-client';

function buildPolishAnimationChunks(prompt: string) {
  const references = readMultimodalReferenceTokens(prompt);
  const plainLength = references.reduce(
    (length, reference) => length - reference.token.length,
    prompt.length
  );
  const chunkSize = Math.max(1, Math.ceil(plainLength / 160));
  const chunks: string[] = [];
  let cursor = 0;

  const addPlainText = (text: string) => {
    for (let i = 0; i < text.length; i += chunkSize) {
      chunks.push(text.slice(i, i + chunkSize));
    }
  };

  for (const reference of references) {
    addPlainText(prompt.slice(cursor, reference.start));
    // Reserved multimodal placeholders must be emitted atomically.
    chunks.push(reference.token);
    cursor = reference.end;
  }
  addPlainText(prompt.slice(cursor));
  return chunks;
}

/** API and UI feedback only. It never translates image-reference syntax. */
export function useWorkspacePromptPolish(params: {
  prompt: string;
  images: WorkspaceReferenceImage[];
  polishUndoLabel: string;
  polishSucceededLabel: string;
  polishFailedLabel: string;
  onPromptChange: (prompt: string) => void;
  onPolishingChange?: (isPolishing: boolean) => void;
}) {
  const {
    prompt,
    images,
    polishUndoLabel,
    polishSucceededLabel,
    polishFailedLabel,
    onPromptChange,
    onPolishingChange,
  } = params;
  const [isPolishing, setIsPolishing] = useState(false);
  const [isPolishRequestPending, setIsPolishRequestPending] = useState(false);
  const typewriterTimerRef = useRef<number | null>(null);

  const imageUrls = useMemo(
    () =>
      images.flatMap((image) =>
        image.uploadStatus === 'uploaded' && image.inputUrl
          ? [image.inputUrl]
          : []
      ),
    [images]
  );
  const canPolish =
    prompt.trim().length > 0 &&
    imageUrls.length === images.length &&
    !isPolishing;

  useEffect(
    () => () => {
      if (typewriterTimerRef.current !== null) {
        window.clearInterval(typewriterTimerRef.current);
      }
    },
    []
  );

  const animatePrompt = useCallback(
    (nextPrompt: string) =>
      new Promise<void>((resolve) => {
        if (typewriterTimerRef.current !== null) {
          window.clearInterval(typewriterTimerRef.current);
        }
        const chunks = buildPolishAnimationChunks(nextPrompt);
        if (chunks.length === 0) {
          onPromptChange(nextPrompt);
          resolve();
          return;
        }
        let chunkIndex = 0;
        let current = '';
        onPromptChange('');
        typewriterTimerRef.current = window.setInterval(() => {
          const chunk = chunks[chunkIndex];
          if (chunk === undefined) {
            if (typewriterTimerRef.current !== null) {
              window.clearInterval(typewriterTimerRef.current);
              typewriterTimerRef.current = null;
            }
            resolve();
            return;
          }
          current += chunk;
          chunkIndex += 1;
          onPromptChange(current);
        }, 18);
      }),
    [onPromptChange]
  );

  const handlePolish = useCallback(async () => {
    if (!canPolish) return;

    const sourcePrompt = prompt;
    setIsPolishing(true);
    setIsPolishRequestPending(true);
    onPolishingChange?.(true);

    try {
      // Exactly the same bytes are used in the editor, API and model prompt.
      const polishedPrompt = await polishWorkspacePrompt({
        prompt: sourcePrompt,
        imageUrls,
      });
      setIsPolishRequestPending(false);

      if (polishedPrompt !== sourcePrompt) {
        await animatePrompt(polishedPrompt);
      }
      toast.success(polishSucceededLabel, {
        ...(polishedPrompt !== sourcePrompt
          ? {
              action: {
                label: polishUndoLabel,
                onClick: () => onPromptChange(sourcePrompt),
              },
            }
          : {}),
      });
    } catch {
      toast.error(polishFailedLabel);
    } finally {
      setIsPolishRequestPending(false);
      setIsPolishing(false);
      onPolishingChange?.(false);
    }
  }, [
    animatePrompt,
    canPolish,
    imageUrls,
    onPolishingChange,
    onPromptChange,
    polishFailedLabel,
    polishSucceededLabel,
    polishUndoLabel,
    prompt,
  ]);

  return { canPolish, isPolishing, isPolishRequestPending, handlePolish };
}
